import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import Box from '@mui/material/Box'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { ISpRewardBrand } from '@brushfire/types'
import { ApiError } from '@/api/http/types'
import { HomeMobilePromo } from '@/features/home/components/HomeMobilePromo'
import { captureRewardRedeemed } from '@/lib/analytics'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { runDetached } from '@/shared/lib/runDetachedPromise'
import { RewardBalanceCardSkeleton } from '@/shared/components/skeletons'
import { RiSearchLine } from '@/shared/icons/remix'
import { useToast } from '@/shared/toast'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  useRedeemSpReward,
  useSpProfile,
  useSpRewardBrands,
  useSpRewardsSummary,
} from '../api'
import {
  EKYC_UNDER_REVIEW_BLOCK_MESSAGE,
  isEkycUnderReview,
} from '@/features/ekyc/lib/ekycUi'
import { useSpKycConfiguration } from '@/features/kyc-config'
import { shouldBlockRedeemForTenantEkyc } from '../lib/rewards-redeem-gate'
import { ConfirmRewardModal } from '../components/ConfirmRewardModal'
import { DuplicateRedemptionDialog } from '../components/DuplicateRedemptionDialog'
import { RedeemSuccessDialog } from '../components/RedeemSuccessDialog'
import { SelectBrandBalanceCard } from '../components/SelectBrandBalanceCard'
import { REDEEM_FAILURE_TOAST, REDEEM_SUCCESS_TOAST } from '../lib/rewards-copy'
import {
  selectBrandContentInnerSx,
  selectBrandContentShellSx,
  selectBrandHeroGreetingSx,
  selectBrandHeroShellSx,
  selectBrandHeroSx,
  selectBrandHeroWelcomeSx,
  selectBrandPageRootSx,
  selectBrandSearchFieldSx,
  selectBrandTitleSx,
} from '../styles/selectBrandStyles'
import { renderBrandGridSection } from './selectBrandGridSection'

const BRANDS_PAGE_SIZE = 9
const RATE_LIMIT_STORAGE_KEY = 'bfp_rewards_rate_limit_expires'
const DUPLICATE_WINDOW_MS = 60_000

interface LastRedemption {
  productId: string
  amountUsd: number
  redeemedAt: number
}

function readRateLimitExpiry(): number | null {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_STORAGE_KEY)
    if (!stored) return null
    const value = Number(stored)
    return value > Date.now() ? value : null
  } catch {
    return null
  }
}

function writeRateLimitExpiry(expiresAt: number): void {
  try {
    localStorage.setItem(RATE_LIMIT_STORAGE_KEY, String(expiresAt))
  } catch {
    // localStorage may be unavailable in some environments
  }
}

function clearRateLimitExpiry(): void {
  try {
    localStorage.removeItem(RATE_LIMIT_STORAGE_KEY)
  } catch {
    // ignore
  }
}

/** Parse retryAfterMs from an API error response (HTTP 429 or rate_limit code). */
function parseRateLimitRetryMs(err: unknown): number | null {
  if (!err || typeof err !== 'object') return null
  const e = err as Record<string, unknown>
  const response = e['response'] as Record<string, unknown> | undefined
  if (!response) return null

  if (response['status'] !== 429) {
    const data = response['data'] as Record<string, unknown> | undefined
    const errorCode = data?.['error'] ?? data?.['code']
    if (errorCode !== 'rate_limit' && errorCode !== 'RATE_LIMIT') return null
  }

  // Prefer retryAfterMs from response body
  const data = response['data'] as Record<string, unknown> | undefined
  const retryAfterMs = data?.['retryAfterMs']
  const retryAfterSeconds = data?.['retryAfterSeconds']
  if (typeof retryAfterMs === 'number') return retryAfterMs
  if (typeof retryAfterSeconds === 'number') return retryAfterSeconds * 1000

  // Fall back to Retry-After header (seconds)
  const headers = response['headers'] as Record<string, unknown> | undefined
  const retryAfterHeader = headers?.['retry-after']
  if (typeof retryAfterHeader === 'string') {
    const secs = Number(retryAfterHeader)
    if (!Number.isNaN(secs)) return secs * 1000
  }

  // Default: 5 minutes
  return 5 * 60 * 1000
}

function greetingFirstName(displayName: string | undefined): string {
  const part = displayName?.trim().split(/\s+/)[0]
  return part || 'there'
}

export function SelectBrandPage() {
  useDocumentTitle('Select a Brand')
  const navigate = useNavigate()
  const { showToast } = useToast()
  const userName = useSessionStore((s) => s.user?.name)
  const firstName = greetingFirstName(userName)

  // ── Brand grid state ───────────────────────────────────────────────────────
  const [search, setSearch] = useState('')
  const [visibleCount, setVisibleCount] = useState(BRANDS_PAGE_SIZE)

  // Reset visible count whenever search term changes
  useEffect(() => {
    setVisibleCount(BRANDS_PAGE_SIZE)
  }, [search])

  // ── Redemption flow state ──────────────────────────────────────────────────
  const [selectedBrand, setSelectedBrand] = useState<ISpRewardBrand | null>(
    null
  )
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [redeemSubmissionError, setRedeemSubmissionError] = useState<
    string | null
  >(null)
  const [redeemSuccessOpen, setRedeemSuccessOpen] = useState(false)

  // ── Duplicate detection state (TC-12B-56–61) ──────────────────────────────
  const [lastRedemption, setLastRedemption] = useState<LastRedemption | null>(
    null
  )
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const pendingAmountRef = useRef<number | null>(null)

  // ── Rate limit state (TC-12B-50–55) ───────────────────────────────────────
  const [rateLimitExpiresAt, setRateLimitExpiresAt] = useState<number | null>(
    readRateLimitExpiry
  )

  // Clear stale rate limit on mount if it has already expired
  useEffect(() => {
    const stored = readRateLimitExpiry()
    if (!stored) {
      setRateLimitExpiresAt(null)
      clearRateLimitExpiry()
    }
  }, [])

  // ── Queries & mutations ───────────────────────────────────────────────────
  const { data: summary, isLoading: summaryLoading } = useSpRewardsSummary()
  const { data: brands, isLoading: brandsLoading } = useSpRewardBrands(
    search.trim() || undefined
  )
  const { data: profile } = useSpProfile()
  const { data: tenantKycMode } = useSpKycConfiguration()
  const redeemMutation = useRedeemSpReward()
  const underReview = isEkycUnderReview(profile?.ekycStatus)

  const balance = summary?.availableBalanceUsd ?? 0

  const filteredBrands = useMemo(() => {
    const list = brands ?? []
    const term = search.trim().toLowerCase()
    if (!term) return list
    return list.filter(
      (b) =>
        b.name.toLowerCase().includes(term) ||
        b.description.toLowerCase().includes(term)
    )
  }, [brands, search])

  const visibleBrands = useMemo(
    () => filteredBrands.slice(0, visibleCount),
    [filteredBrands, visibleCount]
  )

  const hasMore = visibleCount < filteredBrands.length

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSelect = (brand: ISpRewardBrand) => {
    if (underReview) {
      showToast(EKYC_UNDER_REVIEW_BLOCK_MESSAGE, 'error')
      return
    }
    if (tenantKycMode === 'unknown') {
      showToast('Rewards are temporarily unavailable. Please try again later.', 'error')
      return
    }
    if (shouldBlockRedeemForTenantEkyc(tenantKycMode, profile)) {
      showToast('Complete identity verification to redeem rewards.', 'error')
      return
    }
    posthog.capture('reward_brand_selected', {
      brand_name: brand.name,
      product_id: brand.productId,
    })
    setSelectedBrand(brand)
    setRedeemSubmissionError(null)
    setConfirmOpen(true)
  }

  /** Performs the actual API call after all guard checks pass. */
  const executeRedeem = async (amountUsd: number) => {
    if (!selectedBrand) return
    if (underReview) {
      showToast(EKYC_UNDER_REVIEW_BLOCK_MESSAGE, 'error')
      return
    }
    if (tenantKycMode === 'unknown') {
      showToast('Rewards are temporarily unavailable. Please try again later.', 'error')
      return
    }
    if (shouldBlockRedeemForTenantEkyc(tenantKycMode, profile)) {
      showToast('Complete identity verification to redeem rewards.', 'error')
      return
    }
    setRedeemSubmissionError(null)
    try {
      await redeemMutation.mutateAsync({
        productId: selectedBrand.productId,
        amountUsd,
      })
      captureRewardRedeemed({
        brandName: selectedBrand.name,
        productId: selectedBrand.productId,
        amountUsd,
      })
      setLastRedemption({
        productId: selectedBrand.productId,
        amountUsd,
        redeemedAt: Date.now(),
      })
      showToast(REDEEM_SUCCESS_TOAST, 'success')
      setRedeemSubmissionError(null)
      setConfirmOpen(false)
      setSelectedBrand(null)
      pendingAmountRef.current = null
      setRedeemSuccessOpen(true)
    } catch (err) {
      if (err instanceof ApiError && err.status === 503 && err.code === 'service_unavailable') {
        showToast(
          err.message || 'Rewards are temporarily unavailable. Please try again later.',
          'error',
        )
        setConfirmOpen(false)
        return
      }
      if (err instanceof ApiError && err.code === 'ekyc_under_review') {
        showToast(err.message || EKYC_UNDER_REVIEW_BLOCK_MESSAGE, 'error')
        setConfirmOpen(false)
        return
      }
      const retryMs = parseRateLimitRetryMs(err)
      if (retryMs === null) {
        // TC-12A-48 — keep modal open and surface the required copy in-modal + toast.
        setRedeemSubmissionError(REDEEM_FAILURE_TOAST)
        setConfirmOpen(true)
        showToast(REDEEM_FAILURE_TOAST, 'error')
      } else {
        // TC-12B-51: rate limit hit — store expiry and surface countdown in modal
        const expiresAt = Date.now() + retryMs
        setRateLimitExpiresAt(expiresAt)
        writeRateLimitExpiry(expiresAt)
      }
    }
  }

  /** Called by ConfirmRewardModal when SP clicks Redeem. Checks for duplicate first. */
  const handleRedeem = (amountUsd: number) => {
    if (!selectedBrand) return

    const now = Date.now()
    const isDuplicate =
      lastRedemption !== null &&
      lastRedemption.productId === selectedBrand.productId &&
      lastRedemption.amountUsd === amountUsd &&
      now - lastRedemption.redeemedAt < DUPLICATE_WINDOW_MS

    if (isDuplicate) {
      // TC-12B-56: same brand + amount within 60 s → show confirmation dialog first
      pendingAmountRef.current = amountUsd
      setDuplicateOpen(true)
      return
    }

    runDetached(executeRedeem(amountUsd))
  }

  const handleDuplicateConfirm = () => {
    setDuplicateOpen(false)
    const amount = pendingAmountRef.current
    if (amount !== null) {
      runDetached(executeRedeem(amount))
    }
  }

  const handleDuplicateCancel = () => {
    setDuplicateOpen(false)
    pendingAmountRef.current = null
  }

  return (
    <Box sx={selectBrandPageRootSx} data-testid="provider-select-brand-page">
      <Box sx={selectBrandHeroSx}>
        <Box sx={selectBrandHeroShellSx}>
          <Box sx={selectBrandContentInnerSx}>
            <Typography component="h1" sx={selectBrandHeroGreetingSx}>
              {`Hey, ${firstName}!`}
            </Typography>
            <Typography sx={selectBrandHeroWelcomeSx}>
              Welcome to Brushfire
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={selectBrandContentShellSx}>
        <Box sx={selectBrandContentInnerSx}>
          <Typography component="h2" sx={selectBrandTitleSx}>
            Select a Brand
          </Typography>

          {summaryLoading ? (
            <RewardBalanceCardSkeleton testId="select-brand-balance-skeleton" />
          ) : (
            <SelectBrandBalanceCard balanceUsd={balance} />
          )}

          <TextField
            fullWidth
            placeholder="Search brands..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ 'data-testid': 'rewards-brand-search' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: '12px' }}>
                  <RiSearchLine
                    size={24}
                    aria-hidden
                    color="rgba(13,27,42,0.5)"
                  />
                </InputAdornment>
              ),
            }}
            sx={selectBrandSearchFieldSx}
          />

          {renderBrandGridSection({
            brandsLoading,
            filteredBrands,
            visibleBrands,
            search,
            hasMore,
            onSelect: handleSelect,
            onLoadMore: () =>
              setVisibleCount((prev) => prev + BRANDS_PAGE_SIZE),
          })}

          <HomeMobilePromo />
        </Box>
      </Box>

      <ConfirmRewardModal
        open={confirmOpen}
        brand={selectedBrand}
        balanceUsd={balance}
        isSubmitting={redeemMutation.isPending}
        submissionError={redeemSubmissionError}
        rateLimitExpiresAt={rateLimitExpiresAt}
        onRateLimitExpired={() => {
          setRateLimitExpiresAt(null)
          clearRateLimitExpiry()
        }}
        onClose={() => {
          setConfirmOpen(false)
          setSelectedBrand(null)
          setRedeemSubmissionError(null)
        }}
        onRedeem={handleRedeem}
      />

      <DuplicateRedemptionDialog
        open={duplicateOpen}
        brandName={selectedBrand?.name ?? ''}
        amountUsd={pendingAmountRef.current ?? 0}
        onConfirm={handleDuplicateConfirm}
        onCancel={handleDuplicateCancel}
      />

      <RedeemSuccessDialog
        open={redeemSuccessOpen}
        onConfirm={() => {
          setRedeemSuccessOpen(false)
          navigate('/rewards?tab=claimed')
        }}
      />
    </Box>
  )
}
